import { getDiaryRevision, getDiaryDates } from "@/api/DashboardApi";
import SiteSafetyHealthSummaryApi from "@/api/SiteSafetyHealthSummaryApi";
import DashboardServices from "@/services/DashboardServices";
import ShowRevisionServices from "@/services/ShowRevisionServices";
import { autoIncerateOrder, finOrderIndex, compareEditDateTimeDesc } from "@/services/GlobalServices";
import _ from "lodash";
import moment from "moment";
// initial state
const state = () => ({
  statusEditable: false,
  revisions: DashboardServices.defaultDiaryRevision(),
  initRevisionsState: DashboardServices.defaultDiaryRevision(),
  diaries: [],
  latestDiaryID: "",
  latestEditAs: "",
  orderIdFreeze: {
    co2InSiteWork: 0,
    co2OutSiteWork: 0,
    workRecords: 0,
    inspections: 0,
    carryingIO: 0,
    safetyPlans: 0,
    safetyPlanImprovementPlans: 0,
    workRecordsQualifiedResponsible: {},
    workRecordsEquipments: {},
    workRecordsSafetyInstructions: {},
    co2OutSiteWorkWaste: 0,
    co2OutSiteWorkContructionSoil: 0,
  },
  loggedInUserName: "",
  tempWorkRecord: DashboardServices.defaultWorkRecord(),
  signature: {
    order: -1,
    dataImage: "",
    signFor: "",
  },
  diaryDates: [],
  // NOTE: currentRevision is redundant - serializerDiary already uses diary.revision
  // for expectedRevision in the save payload. Kept for potential future use.
  currentRevision: null,
  // 楽観的ロック競合時のマージ用: ロード時の workRecords の originalRowId 一覧
  loadedOriginalRowIds: [],
});

// getters
const getters = {
  getDataHeader: (state) => {
    return {
      meta: {
        editor: state.revisions.editor,
        state: state.revisions.state,
        diaryDate: new Date(state.revisions.diaryDate),
        diaryPeriod: state.revisions.diaryPeriod,
      },
      meetingDateTime: state.revisions.meetingDateTime,
      weather: state.revisions.weather,
      temperature: {
        highest: state.revisions.temperature.highest,
        lowest: state.revisions.temperature.lowest,
      },
      windSpeed: {
        speed: state.revisions.windSpeed.speed,
        unit: state.revisions.windSpeed.unit,
      },
      responsibleOfSafe: state.revisions.responsibleOfSafe,
    };
  },
  getSitePatrolRecords: (state) => {
    return state.revisions.sitePatrolRecords;
  },
  getAttachedPresignedUrl: (state) => {
    return state.revisions.attachedImagePresignedUrl;
  },
  getAttachedImage: (state) => {
    return state.revisions.attachedImageUrl;
  },
  getSignRecords: (state) => {
    return state.revisions.signs;
  },
  getStatusEditable: (state) => {
    return state.statusEditable;
  },
  getDiaryRevisions: (state) => {
    return state.revisions;
  },
  getDiaries: (state) => {
    return state.diaries;
  },
  getInitRevisionsState: (state) => {
    return state.initRevisionsState;
  },
  getTempWorkRecord: (state) => {
    return state.tempWorkRecord;
  },
  getOrderIdFreeze: (state) => {
    return state.orderIdFreeze;
  },
  getSignature: (state) => {
    return state.signature;
  },
  getLatestEditAs: (state) => {
    return state.latestEditAs;
  },
  getDiaryDates: (state) => {
    return state.diaryDates;
  },
  getPreviousDate: (state) => {
    const currentDate = state.revisions.diaryDate
      ? moment(state.revisions.diaryDate).format("YYYY-MM-DD")
      : null;
    if (!currentDate || state.diaryDates.length === 0) return null;
    const idx = state.diaryDates.indexOf(currentDate);
    if (idx > 0) return state.diaryDates[idx - 1];
    return null;
  },
  getCurrentRevision: (state) => state.currentRevision,
  getLoadedOriginalRowIds: (state) => state.loadedOriginalRowIds,
  getNextDate: (state) => {
    const currentDate = state.revisions.diaryDate
      ? moment(state.revisions.diaryDate).format("YYYY-MM-DD")
      : null;
    if (!currentDate || state.diaryDates.length === 0) return null;
    const idx = state.diaryDates.indexOf(currentDate);
    if (idx >= 0 && idx < state.diaryDates.length - 1)
      return state.diaryDates[idx + 1];
    return null;
  },
};

// actions
const actions = {
  async getDiaryRevisionRecords(
    { commit, state, rootGetters },
    { siteId, objDate, diaryId, diaryIdPrevious, typeEdit, period }
  ) {
    getDiaryRevision(siteId, objDate, period).then((response) => {
      let diariesTemp = response.data.result;
      const diariesAfterDeletePreviousCopy =
        DashboardServices.deleteDiariesBeforeCopy(diariesTemp, diaryId);
      const diaries = DashboardServices.formatDataStructWorkRecord(
        diariesAfterDeletePreviousCopy
      );

      commit("setLatestEditAs", typeEdit);
      if (
        typeEdit == undefined &&
        diaryId != undefined &&
        diaries != undefined
      ) {
        const latestEditAdTemp = _.find(diaries, (diary) => {
          return diary.diaryId == diaryId;
        });
        
        // 該当するdiaryが見つからない場合のエラー防止
        if (latestEditAdTemp && latestEditAdTemp.editAs) {
          commit("setLatestEditAs", latestEditAdTemp.editAs);
        } else {
          commit("setLatestEditAs", "budget"); // デフォルト値を設定
        }
      }
      // 実施入力の場合、DBに保存された打合せ日時を取得しておく
      // （最後の予定入力で保存された値を復元するため）
      let savedMeetingDateTime = null;
      if (typeEdit == "actual" && diaries.length > 0) {
        // 最新のbudgetリビジョンからmeetingDateTimeを取得
        const budgetDiaries = diaries.filter((d) => d.editAs == "budget");
        const latestDiary = budgetDiaries.length > 0
          ? _.maxBy(budgetDiaries, (d) => d.revision)
          : _.maxBy(diaries, (d) => d.revision);
        if (latestDiary && latestDiary.meetingDateTime) {
          savedMeetingDateTime = moment(latestDiary.meetingDateTime);
        }
      }

      commit("setDiaryRevisionRecords", {
        diaries,
        diaryId,
        diaryIdPrevious,
        typeEdit,
        isInitRevision: true,
      });
      commit("setDiaries", { diaries, typeEdit });
      commit("setLastDiaryId", diaryId);
      commit("assignOrderPosition");
      const userName = rootGetters['diaryListRecords/getName'] || "";
      if (userName) {
        commit("setLoggedInUserName", userName);
      }
      if (typeEdit == "actual" || typeEdit == "budget") {
        if (userName) {
          commit("setValueRecord", { path: "editor", value: userName });
        }
        commit("setValueRecord", { path: "diaryPeriod", value: period });
        if (userName) {
          commit("setValueRecord", { path: "responsibleOfSafe", value: userName });
        }
      }
      // 予定入力の場合は打合せ日時を現在日時にリセット
      // 実施入力の場合はDBから取得した値（最後の予定入力で保存した日時）を明示的にセット
      if (typeEdit == "budget") {
        commit("setValueRecord", {
          path: "meetingDateTime",
          value: moment(new Date()),
        });
      } else if (typeEdit == "actual" && savedMeetingDateTime) {
        commit("setValueRecord", {
          path: "meetingDateTime",
          value: savedMeetingDateTime,
        });
      }

      // preventionMeasures から safetyPlans へのプリフィル
      const monthStr = String(objDate.month).padStart(2, '0');
      const targetMonth = `${objDate.year}-${monthStr}-01`;
      SiteSafetyHealthSummaryApi.getSummary({ siteId, targetMonth })
        .then((summaryResponse) => {
          const summary = summaryResponse.data;
          if (summary && summary.preventionMeasures) {
            const currentSafetyPlans = state.revisions.safetyPlans;
            if (!currentSafetyPlans || currentSafetyPlans.length === 0) {
              const lines = summary.preventionMeasures
                .split('\n')
                .filter(line => line.trim() !== '')
                .slice(0, 6);
              lines.forEach((line) => {
                commit('addNewRecord', 'safetyPlans');
                const lastIndex = state.revisions.safetyPlans.length - 1;
                commit('setValueRecord', {
                  path: ['safetyPlans', lastIndex, 'plan'],
                  value: line.trim()
                });
              });
            }
          }
        })
        .catch((error) => {
          console.error('Failed to fetch safety health summary:', error);
        });
    });
  },
  async fetchDiaryDates({ commit }, { siteId, period }) {
    try {
      const response = await getDiaryDates(siteId, period);
      if (response.data && response.data.result) {
        commit("setDiaryDates", response.data.result);
      }
    } catch (error) {
      console.error("Failed to fetch diary dates:", error);
    }
  },
};

// mutations
const mutations = {
  addNewRecord(state, type) {
    const listTables = [
      "inspections",
      "carryingIO",
      "safetyPlans",
      "safetyPlanImprovementPlans",
      "co2InSiteWork",
      "co2OutSiteWork",
      "co2OutSiteWorkWaste",
      "co2OutSiteWorkContructionSoil",
    ];
    if (listTables.includes(type)) {
      const valueTemp = ShowRevisionServices.createTemplatePreviousValue(type);
      const previousValueTemp = _.cloneDeep(valueTemp);
      const styleType = state.latestEditAs === "actual" && state.diaries.length > 1 ? "StyleEditActual" : null;
      const styleTemp = ShowRevisionServices.createTemplate(type, styleType);
      const isFocusTemp = ShowRevisionServices.createTemplate(
        type,
        "StatusFocus"
      );
      const orderId = autoIncerateOrder(
        state.orderIdFreeze[type],
        state.revisions[type]
      );
      const recordTemp = Object.assign(
        valueTemp,
        { order: orderId },
        { style: styleTemp },
        { previousItem: previousValueTemp },
        { isFocus: isFocusTemp }
      );
      state.revisions[type].push(recordTemp);
    }
  },
  //WorkRecord
  setWorkContentModalRecord(state, obj) {
    const styleWorkRecordTemp =
      ShowRevisionServices.createTemplate("workRecords");
    const orderId = autoIncerateOrder(
      state.orderIdFreeze["workRecords"],
      state.revisions.workRecords
    );
    const record = _.cloneDeep(obj);
    const workRecordTemp = Object.assign(record, {
      order: orderId,
      orderPosition: orderId,
      style: styleWorkRecordTemp,
    });
    // originalRowIdが既に存在しない場合は、objから取得するかUUIDを生成
    if (!workRecordTemp.originalRowId) {
      const { generateUUID } = require('@/services/GlobalServices');
      workRecordTemp.originalRowId = obj.originalRowId || generateUUID();
    }
    state.revisions.workRecords.push(workRecordTemp);
  },
  setValueRecord(state, obj) {
    _.set(state.revisions, obj.path, obj.value);
  },
  setLoggedInUserName(state, userName) {
    state.loggedInUserName = userName;
  },
  setStatusEditable(state, value) {
    state.statusEditable = value;
  },
  setInitRevisionsState(state, revision) {
    state.initRevisionsState = _.cloneDeep(revision);
  },
  setDiaryRevisionRecords(state, objDiaries) {
    const diaries = _.cloneDeep(objDiaries.diaries);
    const diaryId = objDiaries.diaryId;
    const diaryIdPrevious = objDiaries.diaryIdPrevious;
    const typeEdit = objDiaries.typeEdit;
    const isInitRevision = objDiaries.isInitRevision;

    const itemsCheckingRevision = [
      "workRecords",
      "inspections",
      "carryingIO",
      "safetyPlans",
      "safetyPlanImprovementPlans",
      "commons",
    ];

    let diaryDate = state.revisions.diaryDate;
    // 実施入力の場合、APIから取得したダイアリーのmeetingDateTimeを保存しておく
    // serializerDiaryRevision 後の Object.assign 等で失われる可能性があるため
    let savedMeetingDateTime = null;
    if (typeEdit == "actual" && diaries.length > 0) {
      const latestDiary = _.maxBy(diaries, (d) => d.revision);
      if (latestDiary && latestDiary.meetingDateTime) {
        savedMeetingDateTime = moment(latestDiary.meetingDateTime);
      }
    }
    try {
      if (diaries.length != 0) {
        state.revisions = ShowRevisionServices.serializerDiaryRevision(
          diaries,
          diaryId,
          diaryIdPrevious
        );
        if (diaryIdPrevious != undefined) {
          const swapRevision = ShowRevisionServices.swapDiarySerializer(
            diaries,
            diaryId,
            diaryIdPrevious,
            state.initRevisionsState
          );
          for (const attr of itemsCheckingRevision) {
            if (attr === "workRecords") {
              // workRecordsの場合はoriginalRowIdとorderPositionを保持しながらマージ
              if (swapRevision[attr] && state.revisions[attr]) {
                const preservedWorkRecords = state.revisions[attr];
                state.revisions[attr] = swapRevision[attr];
                // originalRowIdとorderPositionを復元
                for (let i = 0; i < state.revisions[attr].length; i++) {
                  if (preservedWorkRecords[i]) {
                    if (preservedWorkRecords[i].originalRowId) {
                      state.revisions[attr][i].originalRowId = preservedWorkRecords[i].originalRowId;
                    }
                    if (preservedWorkRecords[i].orderPosition !== undefined) {
                      state.revisions[attr][i].orderPosition = preservedWorkRecords[i].orderPosition;
                    }
                  }
                }
              } else {
                state.revisions[attr] = swapRevision[attr];
              }
            } else {
              state.revisions[attr] = swapRevision[attr];
            }
          }
        }
        
        // 既存のworkRecordsにoriginalRowIdがない場合は生成して設定
        if (state.revisions.workRecords && Array.isArray(state.revisions.workRecords)) {
          state.revisions.workRecords.forEach((workRecord) => {
            const recordOriginalRowId = workRecord.record?.originalRowId;

            // record内またはトップレベルにoriginalRowIdが存在する場合は両方に同じ値を設定
            const existingOriginalRowId = recordOriginalRowId || workRecord.originalRowId;
            if (existingOriginalRowId) {
              workRecord.originalRowId = existingOriginalRowId;
              if (workRecord.record) {
                workRecord.record.originalRowId = existingOriginalRowId;
              }
            } else {
              // どちらにもoriginalRowIdがない場合のみ生成
              const newUuid = require('@/services/GlobalServices').generateUUID();
              workRecord.originalRowId = newUuid;
              if (workRecord.record) {
                workRecord.record.originalRowId = newUuid;
              }
            }
          });
        }
        
        state.orderIdFreeze = DashboardServices.freezeMaxIdOrder(state);
      } else {
        state.revisions = DashboardServices.defaultDiaryRevision(typeEdit);
      }
    } catch (errors) {
      state.revisions = DashboardServices.defaultDiaryRevision();
    }

    state.revisions.diaryDate = diaryDate;
    // 実施入力の場合、DBから取得した打合せ日時を確実に復元する
    if (typeEdit == "actual" && savedMeetingDateTime) {
      state.revisions.meetingDateTime = savedMeetingDateTime;
    }
    // 入力者・安全当番が空の場合、ログインユーザー名をデフォルト設定
    if (!state.revisions.editor && state.loggedInUserName) {
      state.revisions.editor = state.loggedInUserName;
    }
    if (!state.revisions.responsibleOfSafe && state.loggedInUserName) {
      state.revisions.responsibleOfSafe = state.loggedInUserName;
    }
    // Store current revision for optimistic locking
    if (state.diaries.length > 0) {
      const sortedForRevision = _.cloneDeep(state.diaries).sort(
        (a, b) => (b.revision || 0) - (a.revision || 0)
      );
      state.currentRevision = sortedForRevision[0].revision || null;
    }

    // ロード時の workRecords の originalRowId を記録（競合マージ用）
    state.loadedOriginalRowIds = (state.revisions.workRecords || [])
      .map(wr => wr.originalRowId)
      .filter(id => !!id);

    isInitRevision &&
      this.commit("dashboardRecords/setInitRevisionsState", state.revisions);
  },
  setDiaries(state, objDiaries) {
    const diaries = objDiaries.diaries;
    if (diaries.length != 0) {
      //save latest diary id
      state.diaries = [];
      let diariesTemp = [];

      // 最新のリビジョンを取得
      // 同じ revision の diary がある場合は editDateTime で降順ソートして最新を取得
      const sortedDiaries = _.cloneDeep(diaries).sort(
        (a, b) => {
          // まず revision で降順ソート
          if (b.revision !== a.revision) {
            return b.revision - a.revision;
          }
          // revision が同じ場合は editDateTime で降順ソート
          return compareEditDateTimeDesc(a, b);
        }
      );
      const latestDiary = _.clone(sortedDiaries[0]);

      // 最新の日記にフラグを設定
      latestDiary.flagDiaryId = "tempDiaryID";
      diariesTemp.push(latestDiary);

      // 最新以外の日記を追加（重複を避けるため）
      sortedDiaries.slice(1).forEach((diary) => {
        diariesTemp.push(diary);
      });

      state.diaries = diariesTemp;

      // 最新の保存済み diary の diaryId を latestDiaryID に設定
      // recalculateRevision で latestDiaryID は「前の diary」として使われる
      // 編集中（新規作成）の場合、最新の保存済み diary が「前の diary」になる
      state.latestDiaryID = sortedDiaries[0].diaryId;
    }
  },
  setLastDiaryId(state, latestDiaryID) {
    state.latestDiaryID = latestDiaryID;
  },
  setLatestEditAs(state, latestEditAs) {
    state.latestEditAs = latestEditAs;
  },
  changeStatusFocus(state, obj) {
    const path = [obj.attr, obj.index, "isFocus", obj.subAttr];
    _.set(state.revisions, path, obj.status);
  },
  changeStatusFocusCommons(state, obj) {
    _.set(state.revisions.commons["isFocus"], obj.attr, obj.status);
  },
  setDataHeaderSigns(state, obj) {
    const i = _.findIndex(state.revisions.signs, (sign) => {
      return sign.order == obj.order;
    });
    _.set(state.revisions, ["signs", i, "sign", obj.attr], obj.value);
  },
  setSitePatrolRecords(state, obj) {
    const sitePatrolRecords = state.revisions.sitePatrolRecords;
    for (let i in sitePatrolRecords) {
      const sitePatrolRecord = sitePatrolRecords[i].site;
      const j = _.findIndex(sitePatrolRecord, (site) => {
        return site.number == obj.number;
      });
      _.set(
        state.revisions,
        ["sitePatrolRecords", i, "site", j, "result"],
        obj.value
      );
    }
  },
  /**
   * サイトパトロール記録のカスタムラベルを設定
   * @param {object} state - Vuex state
   * @param {object} obj - { number: 項目番号, customLabel: カスタムラベル値 }
   */
  setSitePatrolCustomLabel(state, obj) {
    const sitePatrolRecords = state.revisions.sitePatrolRecords;
    for (let i in sitePatrolRecords) {
      const sitePatrolRecord = sitePatrolRecords[i].site;
      const j = _.findIndex(sitePatrolRecord, (site) => {
        return site.number == obj.number;
      });
      if (j >= 0) {
        _.set(
          state.revisions,
          ["sitePatrolRecords", i, "site", j, "customLabel"],
          obj.customLabel
        );
      }
    }
  },
  setTempWorkRecord(state, obj) {
    state.tempWorkRecord = obj.tempWorkRecord;
  },
  setDataInputWorkModal(state, obj) {
    const attr = obj.attr.split(".");
    if (obj.index == -1) {
      //case happen when obj.attr is not equipment or safetyInstructions
      _.set(state.tempWorkRecord, obj.attr, obj.value);
    } else if (attr.length > 1) {
      //path is an array
      const path = [attr[0], obj.index, attr[1]].join(".");
      _.set(state.tempWorkRecord, path, obj.value);
    }
  },
  updateWorkContentModalRecord(state, workRecord) {
    const attributeWorkContents = [
      "primaryConstractor",
      "subContractor",
      "workType",
      "workDescription",
      "sign",
      "presignedUrl",
      "contractCompanyId",
      "qualificatedResponsible",
      "qualificatedResponsible",
      "numberOfPeople.budget",
      "numberOfPeople.actual",
      "useOfFire",
      "equipments",
      "safetyInstructions",
      "originalRowId",
      "orderPosition",
    ];
    for (const i in state.revisions.workRecords) {
      let record = state.revisions.workRecords[i];
      if (
        record.order == workRecord.order &&
        record.order != -1 &&
        record.order != undefined
      ) {
        const indexRecord = finOrderIndex(
          state.revisions.workRecords,
          record.order
        );
        for (let attr of attributeWorkContents) {
          _.set(
            state.revisions.workRecords[indexRecord],
            attr,
            _.get(workRecord, attr, "")
          );
        }
        break;
      }
    }
  },
  addQualificatedResponsibleField(state, obj) {
    state.tempWorkRecord.qualificatedResponsible.push(obj);
  },
  removeQuanlificatedResponsible(state, index) {
    state.tempWorkRecord.qualificatedResponsible.splice(index, 1);
  },
  addUsedEquipmentField(state, obj) {
    state.tempWorkRecord.equipments.push(obj);
  },
  removeUsedEquipmentField(state, index) {
    state.tempWorkRecord.equipments.splice(index, 1);
  },
  addSafetyInstructions(state, safetyInstruction) {
    state.tempWorkRecord.safetyInstructions.push(safetyInstruction);
  },
  removeSafetyInstructions(state, index) {
    state.tempWorkRecord.safetyInstructions.splice(index, 1);
  },
  setUrlImage(state, imageURL) {
    state.revisions.attachedImageUrl = imageURL;
  },
  setPresignedUrlImage(state, imageURL) {
    state.revisions.attachedImagePresignedUrl = imageURL;
  },
  setObjectKeyImage(state, objectKey) {
    state.revisions.attachedImageObjectKey = objectKey;
  },
  deleteRecord(state, { attr, orderId }) {
    const revisions = state.revisions;
    const indexDel = finOrderIndex(revisions[attr], orderId);
    revisions[attr].splice(indexDel, 1);
  },
  changePositionWorkDialog(state, { index, orderPosition }) {
    _.set(state.revisions.workRecords, [index, "orderPosition"], orderPosition);
    state.revisions.workRecords = _.orderBy(
      state.revisions.workRecords,
      ["orderPosition"],
      ["asc"]
    );
  },
  //use to show revision directly when editing
  recalculateRevision(state) {
    const diaries = state.diaries;
    if (diaries.length > 1) {
      const diaryId = "tempDiaryID";
      const typeEditing = state.latestEditAs;

      const diary = DashboardServices.serializerDiary(
        state.revisions,
        state.revisions.siteId,
        state.revisions.editAs,
        true
      );

      // workRecordsのorderPositionのみを復元（style情報は再計算させる）
      const serializedWorkRecords = diary.workRecords;
      const originalWorkRecords = state.revisions.workRecords;

      for (let i = 0; i < serializedWorkRecords.length; i++) {
        if (originalWorkRecords[i]) {
          // orderPositionを復元（トップレベルに設定）
          if (originalWorkRecords[i].orderPosition !== undefined) {
            serializedWorkRecords[i].orderPosition = originalWorkRecords[i].orderPosition;
          }

          const nestedFields = ['qualificatedResponsible', 'equipments', 'safetyInstructions'];
          nestedFields.forEach(fieldName => {
            if (originalWorkRecords[i][fieldName] && serializedWorkRecords[i].record[fieldName]) {
              const originalNested = originalWorkRecords[i][fieldName];
              const serializedNested = serializedWorkRecords[i].record[fieldName];

              for (let j = 0; j < Math.min(serializedNested.length, originalNested.length); j++) {
                if (originalNested[j] && originalNested[j].order !== undefined) {
                  serializedNested[j].order = originalNested[j].order;
                }
              }
            }
          });
        }
      }

      // diaries[0] が既に recalculateRevision で差し替え済みかどうかをチェック
      // 注意: setDiaries で flagDiaryId = "tempDiaryID" が設定されるが、
      // diaryId = "tempDiaryID" は recalculateRevision でのみ設定される。
      // flagDiaryId だけで判定すると、初回の recalculateRevision で元の diary（最新の
      // 保存済みリビジョン）が diaries 配列から消失し、比較対象がコピー元の古い
      // リビジョンになってしまうため、コピー後の予定入力で編集した箇所がすべて
      // 赤字表示される不具合が発生する。diaryId のみで判定することで修正。
      const isAlreadyEditing = state.diaries[0].diaryId === "tempDiaryID";

      let diaryIdPrevious;

      if (!isAlreadyEditing) {
        // 初回の編集：元の diaries[0]（最新の保存済み diary）を保存
        const originalLatestDiary = _.cloneDeep(state.diaries[0]);
        // flagDiaryId を削除して通常の diary として扱えるようにする
        delete originalLatestDiary.flagDiaryId;

        // 編集中の内容を diaries[0] に設定
        state.diaries[0] = Object.assign(_.cloneDeep(diary), {
          diaryId: "tempDiaryID",
          flagDiaryId: "tempDiaryID",
          revision: 1000000000,
          editAs: typeEditing,
        });

        // 元の最新 diary を diaries[1] に挿入（前の diary として参照できるようにする）
        state.diaries.splice(1, 0, originalLatestDiary);

        // latestDiaryID を更新（前の diary として使用）
        state.latestDiaryID = originalLatestDiary.diaryId;
        diaryIdPrevious = originalLatestDiary.diaryId;
      } else {
        // 2回目以降の編集：diaries[0] を更新するだけ
        state.diaries[0] = Object.assign(_.cloneDeep(diary), {
          diaryId: "tempDiaryID",
          flagDiaryId: "tempDiaryID",
          revision: 1000000000,
          editAs: typeEditing,
        });

        // diaryIdPrevious は既に設定されている latestDiaryID を使用
        diaryIdPrevious = state.latestDiaryID;
      }
      this.commit("dashboardRecords/setDiaryRevisionRecords", {
        diaries,
        diaryId,
        diaryIdPrevious,
      });
    }
  },
  //add order position when order position of workRecord is null
  assignOrderPosition(state) {
    const workRecords = state.revisions.workRecords;
    if (workRecords != undefined) {
      for (let i in workRecords) {
        const workRecord = workRecords[i];
        if (workRecord.orderPosition == undefined || workRecord.orderPosition === '') {
          _.set(
            state.revisions.workRecords,
            [i, "orderPosition"],
            workRecord.order
          );
        }
      }
    }
    state.revisions.workRecords = _.orderBy(
      state.revisions.workRecords,
      ["orderPosition"],
      ["asc"]
    );
  },
  resetState(state) {
    state.revisions = DashboardServices.defaultDiaryRevision();
    state.initRevisionsState = DashboardServices.defaultDiaryRevision();
    state.statusEditable = false;
    state.diaries = [];
    state.latestDiaryID = "";
    state.latestEditAs = "";
    state.orderIdFreeze = {
      co2InSiteWork: 0,
      co2OutSiteWork: 0,
      workRecords: 0,
      inspections: 0,
      carryingIO: 0,
      safetyPlans: 0,
      safetyPlanImprovementPlans: 0,
      workRecordsQualifiedResponsible: {},
      workRecordsEquipments: {},
      workRecordsSafetyInstructions: {},
      co2OutSiteWorkWaste: 0,
      co2OutSiteWorkContructionSoil: 0,
    };

    state.tempWorkRecord = DashboardServices.defaultWorkRecord();
    state.signature = {
      order: -1,
      dataImage: "",
      signFor: "",
    };
    state.diaryDates = [];
    state.currentRevision = null;
    state.loadedOriginalRowIds = [];
  },
  setDiaryDates(state, dates) {
    state.diaryDates = dates;
  },
  setSignature(state, signature) {
    state.signature = signature;
  },
  // 競合マージ用: サーバー最新データ全体 + 自分の追加 workRecords を統合
  mergeDiaryAfterConflict(state, { serverDiary, localAdditions }) {
    const serverWorkRecords = serverDiary.workRecords || [];
    const merged = [...serverWorkRecords, ...localAdditions];
    // orderPosition を再採番
    merged.forEach((wr, index) => {
      wr.orderPosition = index + 1;
      wr.order = index + 1;
    });
    // workRecords 以外のフィールドもサーバー最新で更新（他ユーザーの変更を尊重）
    const diaryDate = state.revisions.diaryDate; // diaryDate はルートクエリ由来なので保持
    state.revisions = {
      ...state.revisions,
      ...serverDiary,
      workRecords: merged,
      diaryDate: diaryDate,
    };
    state.currentRevision = serverDiary.revision || null;
    // 注意: loadedOriginalRowIds はここでは更新しない（保存成功前に更新するとリトライ時に追加分が消えるため）
  },
  // 保存成功後に loadedOriginalRowIds を現在の workRecords で更新
  updateLoadedOriginalRowIds(state) {
    state.loadedOriginalRowIds = (state.revisions.workRecords || [])
      .map(wr => wr.originalRowId)
      .filter(id => !!id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
